import { Button } from "@package/subframe/components/Button";
import { IconButton } from "@package/subframe/components/IconButton";
import { FeatherArrowLeft, FeatherPaperclip } from "@subframe/core";
import { format } from "date-fns";
import { useState } from "react";
import { toast } from "sonner";

import { Calendar } from "@/components/ui/calendar";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { useAttachEvidenceToInstanceCommandControllerAttach } from "@/generated/compliance-tasks/compliance-tasks";
import type { ComplianceTaskDefinitionResponseDto } from "@/generated/model/complianceTaskDefinitionResponseDto";
import type { ComplianceTaskInstanceResponseDto } from "@/generated/model/complianceTaskInstanceResponseDto";
import { useOfflineComplianceCompletion } from "@/hooks/use-offline-compliance-completion";
import { parseApiDateToLocalMidnight } from "@/lib/date-utils";

import { EvidenceUploadField } from "./evidence-upload-field";

interface Props {
  instance: ComplianceTaskInstanceResponseDto;
  definition: ComplianceTaskDefinitionResponseDto;
  onBack: () => void;
  onAttached: () => Promise<void> | void;
}

/**
 * Rendered when an instance is in AWAITING_EVIDENCE — the task body was
 * submitted earlier but no certificate was attached. The practitioner (or
 * any member who can update the instance) returns here later to upload the
 * cert and supply the next due date, which together promote the instance
 * to COMPLETED on the backend.
 *
 * The full completion form is intentionally NOT re-rendered here — the
 * audit trail of the original submission stays untouched. Only the cert,
 * the next due date, and optional appended notes are captured.
 */
export function AwaitingCertificateView({
  instance,
  definition,
  onBack,
  onAttached,
}: Props) {
  const completion = useOfflineComplianceCompletion(
    instance.id,
    instance.nurseryId,
  );

  // Pre-fill with the date captured at submit time, falling back to the
  // window's target (latest allowed) when none was captured. The practitioner
  // can edit it before final submit (cert expiry might differ from their guess).
  const [nextScheduledDate, setNextScheduledDate] = useState<Date | undefined>(
    instance.pendingNextDueDate
      ? parseApiDateToLocalMidnight(instance.pendingNextDueDate)
      : instance.nextDueWindow
        ? parseApiDateToLocalMidnight(instance.nextDueWindow.target)
        : undefined,
  );
  const [additionalNotes, setAdditionalNotes] = useState("");
  // The certificate's expiry can legitimately fall outside the recurrence
  // window (NAU-302). Rather than hard-block a real cert date, we warn and
  // require an explicit confirmation that is recorded as an audited override.
  const [confirmOverride, setConfirmOverride] = useState(false);

  // Parse the bounds to LOCAL midnight so they compare correctly against the
  // calendar's local-midnight day cells (the API serializes them as UTC midnight).
  const nextDueWindow = instance.nextDueWindow ?? null;
  const windowEarliest = nextDueWindow
    ? parseApiDateToLocalMidnight(nextDueWindow.earliest)
    : undefined;
  const windowTarget = nextDueWindow
    ? parseApiDateToLocalMidnight(nextDueWindow.target)
    : undefined;
  const isOutsideWindow = !!(
    nextScheduledDate &&
    ((windowEarliest && nextScheduledDate < windowEarliest) ||
      (windowTarget && nextScheduledDate > windowTarget))
  );

  const attachMutation =
    useAttachEvidenceToInstanceCommandControllerAttach<Error>();

  const handleSubmit = async () => {
    // Narrows for the now-required field; canSubmit already gates the button on
    // it, so this isn't reachable from the UI.
    if (!nextScheduledDate) return;

    try {
      await attachMutation.mutateAsync({
        instanceId: instance.id,
        data: {
          nextDueDate: format(nextScheduledDate, "yyyy-MM-dd"),
          additionalNotes: additionalNotes.trim() || undefined,
          // Record the audited override when the chosen date is out of window.
          overrideWindow: isOutsideWindow ? true : undefined,
        },
      });
      toast.success("Certificate attached — task complete");
      // `onAttached` owns the cache sweep for this path — invalidating here as
      // well would run two full sweeps back to back.
      await onAttached();
    } catch (err) {
      toast.error("Couldn't attach the certificate — try again");
      console.error("attach-evidence failed", err);
    }
  };

  // A file in 'pending' syncState is queued locally but not yet uploaded to the
  // server, so the BE's attach-evidence endpoint would 400 ('upload a
  // certificate before attaching'). Wait for at least one photo to reach
  // 'synced' before enabling submit.
  const hasSyncedEvidence = completion.photos.some(
    (p) => p.syncState === "synced",
  );
  const hasPendingUpload = completion.photos.some(
    (p) => p.syncState === "pending",
  );
  const hasFailedUpload = completion.photos.some(
    (p) => p.syncState === "error",
  );
  // Block submit until a next due date is chosen (NAU-349), and on an
  // out-of-window date until the user explicitly confirms the override, so a
  // cert date beyond the window is a deliberate choice.
  const canSubmit =
    hasSyncedEvidence &&
    nextScheduledDate != null &&
    !attachMutation.isPending &&
    (!isOutsideWindow || confirmOverride);

  return (
    <div className="flex w-full flex-col items-start bg-white animate-fade-in">
      <div className="flex w-full flex-col items-start gap-1 pb-6">
        <div className="flex w-full items-center gap-2">
          <IconButton
            variant="neutral-secondary"
            icon={<FeatherArrowLeft />}
            onClick={onBack}
          />
          <span className="grow shrink-0 basis-0 whitespace-nowrap text-metrics font-metrics text-primary-navy-800">
            {definition.name}
          </span>
        </div>
        <div className="flex items-center gap-2 pl-12">
          <span className="inline-flex items-center gap-1 rounded-full bg-amber-50 px-2 py-0.5 text-caption font-caption text-amber-900">
            <FeatherPaperclip className="h-3 w-3" />
            Awaiting certificate
          </span>
        </div>
        <span className="pl-12 text-body font-body text-subtext-color">
          This task was submitted. Add the certificate when it arrives to
          complete it.
        </span>
      </div>

      <div className="flex w-full max-w-2xl mx-auto flex-col items-start gap-6 pb-32">
        <EvidenceUploadField
          photos={completion.photos}
          onAddPhoto={completion.savePhoto}
          onDeletePhoto={completion.deletePhoto}
          required={true}
        />

        <div className="flex w-full flex-col items-start gap-2">
          <label className="text-body-bold font-body-bold text-neutral-900">
            Next due date
            <span className="text-error-700 ml-1">*</span>
          </label>
          <span className="text-caption font-caption text-subtext-color">
            {windowEarliest && windowTarget
              ? `Typically the certificate expiry date. The recommended window is ${format(windowEarliest, "d MMM yyyy")}–${format(windowTarget, "d MMM yyyy")}.`
              : "Typically the certificate expiry date. We'll schedule the next instance for this date."}
          </span>
          <Popover>
            <PopoverTrigger asChild>
              <button
                type="button"
                className="flex h-10 w-full items-center justify-between rounded-md border border-solid border-neutral-border bg-default-background px-3 text-body font-body text-neutral-900 hover:border-neutral-400"
              >
                <span>
                  {nextScheduledDate
                    ? format(nextScheduledDate, "EEEE, MMMM d, yyyy")
                    : "Select date"}
                </span>
                <svg
                  className="h-4 w-4 text-neutral-500"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M19 9l-7 7-7-7"
                  />
                </svg>
              </button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0" align="start">
              {/* Floor-only (no past dates) even when a window exists: a real
                  certificate's expiry may legitimately fall outside the window,
                  so the out-of-window case is handled by the warn + confirm
                  override below rather than by disabling those days. */}
              <Calendar
                mode="single"
                selected={nextScheduledDate}
                // Open on the selected/target month, not today — the cert expiry
                // window is typically a year out.
                defaultMonth={nextScheduledDate ?? windowTarget}
                onSelect={(date) => {
                  // Any date change invalidates a prior override confirmation —
                  // the audited override must always refer to the shown date.
                  setNextScheduledDate(date);
                  setConfirmOverride(false);
                }}
                disabled={(date) =>
                  date < new Date(new Date().setHours(0, 0, 0, 0))
                }
                autoFocus
              />
            </PopoverContent>
          </Popover>

          {isOutsideWindow && (
            <label className="flex w-full items-start gap-2 rounded-md border border-solid border-amber-200 bg-amber-50 px-3 py-2">
              <input
                type="checkbox"
                checked={confirmOverride}
                onChange={(e) => setConfirmOverride(e.target.checked)}
                className="mt-0.5"
              />
              <span className="text-caption font-caption text-amber-900">
                This date is outside the recommended window
                {windowEarliest && windowTarget
                  ? ` (${format(windowEarliest, "d MMM yyyy")}–${format(windowTarget, "d MMM yyyy")})`
                  : ""}
                . If it matches the certificate expiry, tick to schedule it
                anyway — the override will be recorded.
              </span>
            </label>
          )}
        </div>

        <div className="flex w-full flex-col items-start gap-2">
          <label
            className="text-body-bold font-body-bold text-neutral-900"
            htmlFor="additional-notes"
          >
            Additional notes
            <span className="text-caption font-caption text-subtext-color ml-1">
              (optional)
            </span>
          </label>
          <span className="text-caption font-caption text-subtext-color">
            Anything the certificate revealed that should be in the audit trail.
            Appended to the original submission notes.
          </span>
          <textarea
            id="additional-notes"
            value={additionalNotes}
            onChange={(e) => setAdditionalNotes(e.target.value)}
            rows={3}
            className="flex w-full rounded-md border border-solid border-neutral-border bg-default-background px-3 py-2 text-body font-body text-neutral-900 placeholder:text-subtext-color hover:border-neutral-400 focus:outline-none focus:ring-2 focus:ring-primary-navy-300"
          />
        </div>
      </div>

      <div className="fixed bottom-0 left-0 right-0 border-t border-neutral-border bg-white px-4 py-3 shadow-md">
        <div className="mx-auto flex max-w-2xl items-center justify-between gap-3">
          <span className="text-caption font-caption text-subtext-color">
            {hasSyncedEvidence
              ? !nextScheduledDate
                ? "Add the next due date to continue"
                : "Ready to submit"
              : hasPendingUpload
                ? "Uploading certificate…"
                : hasFailedUpload
                  ? "Upload failed — tap the photo to retry"
                  : "Upload the certificate to continue"}
          </span>
          <Button disabled={!canSubmit} onClick={handleSubmit}>
            {attachMutation.isPending ? "Submitting…" : "Submit certificate"}
          </Button>
        </div>
      </div>
    </div>
  );
}
